import { useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

type PageRequirementMap = Partial<Record<number, boolean>>;

type UsePageStateGuardOptions = {
  pageParamName?: string;
  rootPage?: number;
};

const DEFAULT_PAGE_PARAM = "page";
const DEFAULT_ROOT_PAGE = 1;

export function usePageStateGuard(requirements: PageRequirementMap, options?: UsePageStateGuardOptions) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const pageParamName = options?.pageParamName ?? DEFAULT_PAGE_PARAM;
  const rootPage = options?.rootPage ?? DEFAULT_ROOT_PAGE;

  const currentPage = useMemo(() => {
    const page = Number(searchParams.get(pageParamName));

    return Number.isFinite(page) && page > 0 ? page : rootPage;
  }, [pageParamName, rootPage, searchParams]);

  useEffect(() => {
    const pageIsAllowed = requirements[currentPage];

    if (pageIsAllowed === false) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set(pageParamName, String(rootPage));

      navigate({ search: nextParams.toString() }, { replace: true });
    }
  }, [currentPage, navigate, pageParamName, requirements, rootPage, searchParams]);

  return currentPage;
}
